// Admin.jsx

import { useEffect, useState, useMemo, memo } from 'react';
import { getUsers, deleteUser, updateUser, createUser } from '@/services/userService';
import { Modal, Button, Form } from 'react-bootstrap';
import { adminCss, modalCss, buttonCss, inputCss } from '@digital_monorepo/styling';
import useInput from '@digital_monorepo/lib-common/hooks/useInput';

const UserForm = memo(({ formData, onSubmit, onCancel, formType, isSubmitting, hasError }) => {
  const {
    name,
    username,
    email,
    role,
    nameError,
    usernameError,
    emailError,
    roleError,
    onNameChange,
    onUsernameChange,
    onEmailChange,
    onRoleChange,
    onNameBlur,
    onUsernameBlur,
    onEmailBlur,
    onRoleBlur
  } = formData;

  return (
    <form onSubmit={onSubmit} className={adminCss.userForm}>
      <div className={`${nameError ? inputCss.inputInformationError : inputCss.inputInformation}`}>
        <label htmlFor='name'>Name</label>
        <input id='name' type='text' className={`${nameError ? inputCss.inputError : ''}`} value={name} onChange={e => onNameChange(e)} onBlur={onNameBlur} />
        {nameError && <p className={inputCss.errorText}>Name is required</p>}
      </div>

      <div className={`${usernameError ? inputCss.inputInformationError : inputCss.inputInformation}`}>
        <label htmlFor='username'>Username</label>
        <input id='username' type='text' className={`${usernameError ? inputCss.inputError : ''}`} value={username} onChange={e => onUsernameChange(e)} onBlur={onUsernameBlur} />
        {usernameError && <p className={inputCss.errorText}>Username is required</p>}
      </div>

      <div className={`${emailError ? inputCss.inputInformationError : inputCss.inputInformation}`}>
        <label htmlFor='email'>Email</label>
        <input id='email' type='email' className={`${emailError ? inputCss.inputError : ''}`} value={email} onChange={e => onEmailChange(e)} onBlur={onEmailBlur} />
        {emailError && <p className={inputCss.errorText}>Valid email is required</p>}
      </div>

      {formType === 'add' && (
        <div className={`${inputCss.inputInformation}`}>
          <label htmlFor='password'>Password</label>
          <input id='password' type='password' className={adminCss.formControl} onChange={e => formData.onPasswordChange(e)} onBlur={formData.onPasswordBlur} />
          {formData.passwordError && <p className={inputCss.errorText}>Password is required</p>}
        </div>
      )}

      <div className={`${roleError ? inputCss.inputInformationError : inputCss.inputInformation}`}>
        <label htmlFor='role'>Role</label>
        <select id='role' className={`${roleError ? inputCss.inputError : ''}`} value={role} onChange={e => onRoleChange(e)} onBlur={onRoleBlur}>
          <option value='USER'>USER</option>
          <option value='ADMIN'>ADMIN</option>
        </select>
        {roleError && <p className={inputCss.errorText}>Role is required</p>}
      </div>

      <div className={adminCss.actionButtons}>
        <Button type='submit' variant='success' className={`${buttonCss.primaryBtn} ${adminCss.adminButton}`} disabled={isSubmitting}>
          Save
        </Button>
        <Button variant='secondary' onClick={onCancel} className={`${buttonCss.secondaryBtn} ${adminCss.adminButton}`} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
});

const Admin = ({ data }) => {
  const [users, setUsers] = useState(data || []);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formHasError, setFormHasError] = useState(false);

  // Form input hooks
  const { value: name, isValid: nameIsValid, hasError: nameHasError, valueChangeHandler: nameChangeHandler, inputBlurHandler: nameBlurHandler, reset: resetName } = useInput(value => !!value);

  const {
    value: username,
    isValid: usernameIsValid,
    hasError: usernameHasError,
    valueChangeHandler: usernameChangeHandler,
    inputBlurHandler: usernameBlurHandler,
    reset: resetUsername
  } = useInput(value => !!value);

  const { value: email, isValid: emailIsValid, hasError: emailHasError, valueChangeHandler: emailChangeHandler, inputBlurHandler: emailBlurHandler, reset: resetEmail } = useInput(value => !!value);

  const {
    value: password,
    isValid: passwordIsValid,
    hasError: passwordHasError,
    valueChangeHandler: passwordChangeHandler,
    inputBlurHandler: passwordBlurHandler,
    reset: resetPassword
  } = useInput(value => !!value);

  const { value: role, isValid: roleIsValid, hasError: roleHasError, valueChangeHandler: roleChangeHandler, inputBlurHandler: roleBlurHandler, reset: resetRole } = useInput(value => !!value, 'USER');

  useEffect(() => {
    if (Array.isArray(data)) {
      setUsers(data);
    }
  }, [data]);

  const handleStartAddUser = () => {
    setIsAddingUser(true);
    setFormHasError(false);
    resetName();
    resetUsername();
    resetEmail();
    resetPassword();
    resetRole();
  };

  const handleEditUser = user => {
    setSelectedUser(user);
    setFormHasError(false);
    nameChangeHandler({ target: { value: user.name || '' } });
    usernameChangeHandler({ target: { value: user.username || '' } });
    emailChangeHandler({ target: { value: user.email || '' } });
    roleChangeHandler({ target: { value: user.role || 'USER' } });
  };

  const handleCancel = () => {
    setSelectedUser(null);
    setIsAddingUser(false);
    setFormHasError(false);
  };

  const validateForm = (isAdd = false) => {
    const isValid = nameIsValid && usernameIsValid && emailIsValid && roleIsValid && (!isAdd || passwordIsValid);

    if (!isValid) {
      setFormHasError(true);
    }
    return isValid;
  };

  const handleSave = async event => {
    event.preventDefault();

    const isAdd = !selectedUser;
    if (!validateForm(isAdd)) {
      return;
    }

    setFormSubmitting(true);

    try {
      const userData = {
        name,
        username,
        email,
        role,
        ...(isAdd && { password })
      };

      console.log('Form data being sent:', { ...userData, password: '[REDACTED]' });

      if (isAdd) {
        const createdUser = await createUser(userData);
        console.log('Response from create user:', { ...createdUser, password: '[REDACTED]' });
        setUsers(prevUsers => [createdUser, ...prevUsers]);
        setIsAddingUser(false);
      } else {
        await updateUser(selectedUser.id, { ...userData, id: selectedUser.id });
        setUsers(prevUsers => prevUsers.map(user => (user.id === selectedUser.id ? { ...user, ...userData } : user)));
        setSelectedUser(null);
      }
    } catch (error) {
      console.error('Error saving user:', error);
      setFormHasError(true);
    } finally {
      setFormSubmitting(false);
    }
  };

  const confirmDelete = user => {
    setSelectedUser(user);
    setShowDeleteConfirmModal(true);
  };

  const handleDelete = async () => {
    try {
      await deleteUser(selectedUser.id);
      setUsers(prevUsers => prevUsers.filter(user => user.id !== selectedUser.id));
      setShowDeleteConfirmModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const getFormData = type => ({
    name,
    username,
    email,
    role,
    nameError: nameHasError,
    usernameError: usernameHasError,
    emailError: emailHasError,
    roleError: roleHasError,
    onNameChange: nameChangeHandler,
    onUsernameChange: usernameChangeHandler,
    onEmailChange: emailChangeHandler,
    onRoleChange: roleChangeHandler,
    onNameBlur: nameBlurHandler,
    onUsernameBlur: usernameBlurHandler,
    onEmailBlur: emailBlurHandler,
    onRoleBlur: roleBlurHandler,
    ...(type === 'add' && {
      password,
      passwordError: passwordHasError,
      onPasswordChange: passwordChangeHandler,
      onPasswordBlur: passwordBlurHandler
    })
  });

  return (
    <div className={adminCss.adminContainer}>
      <div className={adminCss.adminHeader}>
        <h2>Workspace members</h2>
        <div className={adminCss.adminActions}>
          <Button variant='primary' onClick={handleStartAddUser} className={`${buttonCss.primaryBtn} ${adminCss.adminButton}`} disabled={isAddingUser || selectedUser}>
            Add User
          </Button>
        </div>
      </div>

      {formHasError && (
        <div className='alert alert-danger' role='alert'>
          Please fix the form errors before submitting.
        </div>
      )}

      {(isAddingUser || selectedUser) && (
        <UserForm
          formData={getFormData(isAddingUser ? 'add' : 'edit')}
          onSubmit={handleSave}
          onCancel={handleCancel}
          formType={isAddingUser ? 'add' : 'edit'}
          isSubmitting={formSubmitting}
          hasError={formHasError}
        />
      )}

      <div className={adminCss.tableResponsive}>
        <table className='table'>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td>{user.id}</td>
                <td>{user.name}</td>
                <td>{user.username}</td>
                <td>{user.email}</td>
                <td>{user.role}</td>
                <td>
                  <div className={adminCss.actionButtons}>
                    <Button variant='primary' onClick={() => handleEditUser(user)} className={`${buttonCss.primaryBtn} ${adminCss.adminButton}`} size='sm' disabled={isAddingUser || selectedUser}>
                      Edit
                    </Button>
                    <Button variant='danger' onClick={() => confirmDelete(user)} className={`${buttonCss.secondaryBtn} ${adminCss.adminButton}`} size='sm' disabled={isAddingUser || selectedUser}>
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && !isAddingUser && <div className={adminCss.noData}>No user data available</div>}
      </div>

      <Modal show={showDeleteConfirmModal} onHide={() => setShowDeleteConfirmModal(false)} centered dialogClassName={adminCss.centeredModal} className={`${modalCss['modal-overlay']}`}>
        <div className={modalCss['modal-content']}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Are you sure you want to delete user: <strong>{selectedUser?.name}</strong>?
            </p>
            <p className='text-danger mt-2'>This action cannot be undone.</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={() => setShowDeleteConfirmModal(false)} className={`${buttonCss.secondaryBtn} ${adminCss.adminButton}`}>
              Cancel
            </Button>
            <Button variant='danger' onClick={handleDelete} className={`${buttonCss.primaryBtn} ${adminCss.adminButton}`}>
              Delete
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
};

export default Admin;
